/**
 * TruncateNode - Geometry modifier that truncates polyhedra
 *
 * Cuts off vertices to create new facets, producing shapes like
 * truncated icosahedron (soccer ball), truncated cube, etc.
 */

import type { BufferGeometry } from 'three';
import { BaseNode } from '@nodes/BaseNode';

import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../../threejs/threejsPlugin';
import { internals, params } from '@/nodes/TypedEndpoints';

const truncateEndpoints = {
  ...internals({
    geometryIn: { dataType: 'three:geometry', isInput: true, label: 'Geometry' },
    geometryOut: { dataType: 'three:geometry', isOutput: true }
  }),
  ...params({
    amount: { value: 0.33, min: 0, max: 1, step: 0.01, label: 'Truncation' },
    weldTolerance: { value: 0.05, min: 0, max: 0.2, step: 0.001, label: 'Weld Tolerance' },
  })


};

interface Vertex {
  x: number;
  y: number;
  z: number;
}

interface Face {
  a: number;
  b: number;
  c: number;
}

class TruncateNode extends BaseNode<typeof truncateEndpoints> {
  private three: any;
  private inputGeometry: BufferGeometry | null = null;

  constructor(id: string, deps: ThreeDependencies) {
    super(id, deps);
    this.three = deps.three;
  }

  onInternalChanged(payload: any, endpointId: string): void {
    if (endpointId === 'geometryIn') {
      this.inputGeometry = payload?.value;
      this.truncate();
    }
  }

  onParamChanged(_payload: any, endpointId: string): void {
    if (endpointId === 'amount' || endpointId === 'weldTolerance') {
      this.truncate();
    }
  }

  private truncate(): void {
    if (!this.inputGeometry || !this.three) return;

    const amount = this.params.amount.value;

    // Extract vertices and faces from input geometry
    const { vertices, faces } = this.extractGeometry(this.inputGeometry);

    if (vertices.length === 0 || faces.length === 0) {
      return;
    }

    // Build edge and vertex connectivity
    const { vertexFaces, edges } = this.buildTopology(vertices, faces);

    // Create truncated geometry
    const truncated = this.computeTruncation(
      vertices, faces, vertexFaces, edges, amount
    );

    // Weld vertices that are very close (handles near-0.5 truncation merging)
    const bounds = this.computeBounds(vertices);
    const size = Math.max(bounds.max.x - bounds.min.x, bounds.max.y - bounds.min.y, bounds.max.z - bounds.min.z);
    const tolerance = size * this.params.weldTolerance.value;

    const { vertices: weldedVerts, faces: weldedFaces } = this.weldVertices(
      truncated.newVertices, truncated.newFaces, tolerance
    );

    // Build output BufferGeometry
   const outputGeometry = this.buildBufferGeometry(weldedVerts, weldedFaces);
    this.internals.geometryOut.set(outputGeometry);
  }

  private extractGeometry(geom: BufferGeometry): { vertices: Vertex[]; faces: Face[] } {
    const vertices: Vertex[] = [];
    const faces: Face[] = [];

    const posAttr = geom.getAttribute('position');
    if (!posAttr) return { vertices, faces };

    const index = geom.getIndex();

    if (index) {
      // Indexed geometry - extract unique vertices
      const vertexMap = new Map<string, number>();

      for (let i = 0; i < index.count; i += 3) {
        const faceIndices: number[] = [];

        for (let j = 0; j < 3; j++) {
          const idx = index.getX(i + j);
          const x = posAttr.getX(idx);
          const y = posAttr.getY(idx);
          const z = posAttr.getZ(idx);

          // Round to avoid floating point issues
          const key = `${x.toFixed(6)},${y.toFixed(6)},${z.toFixed(6)}`;

          if (!vertexMap.has(key)) {
            vertexMap.set(key, vertices.length);
            vertices.push({ x, y, z });
          }
          faceIndices.push(vertexMap.get(key)!);
        }

        faces.push({ a: faceIndices[0], b: faceIndices[1], c: faceIndices[2] });
      }
    } else {
      // Non-indexed geometry
      const vertexMap = new Map<string, number>();

      for (let i = 0; i < posAttr.count; i += 3) {
        const faceIndices: number[] = [];

        for (let j = 0; j < 3; j++) {
          const x = posAttr.getX(i + j);
          const y = posAttr.getY(i + j);
          const z = posAttr.getZ(i + j);

          const key = `${x.toFixed(6)},${y.toFixed(6)},${z.toFixed(6)}`;

          if (!vertexMap.has(key)) {
            vertexMap.set(key, vertices.length);
            vertices.push({ x, y, z });
          }
          faceIndices.push(vertexMap.get(key)!);
        }

        faces.push({ a: faceIndices[0], b: faceIndices[1], c: faceIndices[2] });
      }
    }

    return { vertices, faces };
  }

  private buildTopology(vertices: Vertex[], faces: Face[]): {
    vertexFaces: Map<number, number[]>;
    edges: Map<string, { v1: number; v2: number; faces: number[] }>;
  } {
    const vertexFaces = new Map<number, number[]>();
    const edges = new Map<string, { v1: number; v2: number; faces: number[] }>();

    // Initialize vertex -> faces mapping
    for (let i = 0; i < vertices.length; i++) {
      vertexFaces.set(i, []);
    }

    // Build mappings from faces
    faces.forEach((face, faceIdx) => {
      const verts = [face.a, face.b, face.c];

      // Add face to each vertex's list
      verts.forEach(v => vertexFaces.get(v)!.push(faceIdx));

      // Add edges
      for (let i = 0; i < 3; i++) {
        const v1 = verts[i];
        const v2 = verts[(i + 1) % 3];
        const edgeKey = v1 < v2 ? `${v1}-${v2}` : `${v2}-${v1}`;

        if (!edges.has(edgeKey)) {
          edges.set(edgeKey, { v1: Math.min(v1, v2), v2: Math.max(v1, v2), faces: [] });
        }
        edges.get(edgeKey)!.faces.push(faceIdx);
      }
    });

    return { vertexFaces, edges };
  }

  private computeTruncation(
    vertices: Vertex[],
    faces: Face[],
    vertexFaces: Map<number, number[]>,
    edges: Map<string, { v1: number; v2: number; faces: number[] }>,
    amount: number
  ): { newVertices: Vertex[]; newFaces: Face[] } {
    const newVertices: Vertex[] = [];
    const newFaces: Face[] = [];

    // Compute geometry centroid
    const geometryCentroid = {
      x: vertices.reduce((s, v) => s + v.x, 0) / vertices.length,
      y: vertices.reduce((s, v) => s + v.y, 0) / vertices.length,
      z: vertices.reduce((s, v) => s + v.z, 0) / vertices.length
    };

    // Find neighbors for each vertex (vertices connected by edges)
    const neighbors = new Map<number, Set<number>>();
    for (let i = 0; i < vertices.length; i++) {
      neighbors.set(i, new Set());
    }
    edges.forEach((edge) => {
      neighbors.get(edge.v1)!.add(edge.v2);
      neighbors.get(edge.v2)!.add(edge.v1);
    });

    // Identify convex vertices: those at least as far from centroid as their neighbors
    // A peak sticks out (farther from center), a valley is tucked in (closer to center)
    // For regular polyhedra (all vertices equidistant), all vertices are convex
    const isConvex = new Map<number, boolean>();

    // Compute size for tolerance calculation
    const bounds = this.computeBounds(vertices);
    const size = Math.max(
      bounds.max.x - bounds.min.x,
      bounds.max.y - bounds.min.y,
      bounds.max.z - bounds.min.z
    );
    const tolerance = size * 0.001; // Small tolerance for floating-point comparison

    for (let vIdx = 0; vIdx < vertices.length; vIdx++) {
      const v = vertices[vIdx];
      const distFromCenter = this.distance(v, geometryCentroid);

      const neighborIndices = neighbors.get(vIdx)!;
      if (neighborIndices.size === 0) {
        isConvex.set(vIdx, true);
        continue;
      }

      // Average distance of neighbors from centroid
      let avgNeighborDist = 0;
      for (const ni of neighborIndices) {
        avgNeighborDist += this.distance(vertices[ni], geometryCentroid);
      }
      avgNeighborDist /= neighborIndices.size;

      // Vertex is convex if it's at least as far from center as its neighbors (with tolerance)
      // This ensures regular polyhedra (where all vertices are equidistant) get all vertices truncated
      isConvex.set(vIdx, distFromCenter >= avgNeighborDist - tolerance);
    }

    // Copy original vertices to newVertices (we'll reference them for non-truncated corners)
    const originalVertexOffset = newVertices.length;
    for (const v of vertices) {
      newVertices.push({ ...v });
    }

    // Map: originalVertexIdx -> Map<neighborVertexIdx, newVertexIdx>
    // Only create cut vertices for edges where at least one endpoint is convex
    const cutVertexMap = new Map<number, Map<number, number>>();

    edges.forEach((edge) => {
      const { v1, v2 } = edge;
      const v1Convex = isConvex.get(v1)!;
      const v2Convex = isConvex.get(v2)!;

      if (!v1Convex && !v2Convex) {
        // Neither vertex is convex - no cuts on this edge
        return;
      }

      const p1 = vertices[v1];
      const p2 = vertices[v2];

      if (!cutVertexMap.has(v1)) cutVertexMap.set(v1, new Map());
      if (!cutVertexMap.has(v2)) cutVertexMap.set(v2, new Map());

      if (v1Convex) {
        // Create cut vertex near v1
        const cut1: Vertex = {
          x: p1.x + (p2.x - p1.x) * amount,
          y: p1.y + (p2.y - p1.y) * amount,
          z: p1.z + (p2.z - p1.z) * amount
        };
        const cut1Idx = newVertices.length;
        newVertices.push(cut1);
        cutVertexMap.get(v1)!.set(v2, cut1Idx);
      }

      if (v2Convex) {
        // Create cut vertex near v2
        const cut2: Vertex = {
          x: p2.x + (p1.x - p2.x) * amount,
          y: p2.y + (p1.y - p2.y) * amount,
          z: p2.z + (p1.z - p2.z) * amount
        };
        const cut2Idx = newVertices.length;
        newVertices.push(cut2);
        cutVertexMap.get(v2)!.set(v1, cut2Idx);
      }
    });

    // Create modified faces based on which vertices are truncated
    faces.forEach((face) => {
      const verts = [face.a, face.b, face.c];
      const convexFlags = verts.map(v => isConvex.get(v)!);
      const convexCount = convexFlags.filter(c => c).length;

      if (convexCount === 0) {
        // No vertices truncated - keep original triangle
        newFaces.push({
          a: originalVertexOffset + face.a,
          b: originalVertexOffset + face.b,
          c: originalVertexOffset + face.c
        });
        return;
      }

      // Build polygon vertices going around the face
      const polyVerts: number[] = [];

      for (let i = 0; i < 3; i++) {
        const curr = verts[i];
        const next = verts[(i + 1) % 3];
        const currConvex = convexFlags[i];
        const nextConvex = convexFlags[(i + 1) % 3];

        if (currConvex) {
          // Add cut vertex from curr toward next
          polyVerts.push(cutVertexMap.get(curr)!.get(next)!);
        } else {
          // Add original vertex
          polyVerts.push(originalVertexOffset + curr);
        }

        if (nextConvex) {
          // Add cut vertex from next toward curr (on the edge from curr to next)
          polyVerts.push(cutVertexMap.get(next)!.get(curr)!);
        }
      }

      // Triangulate polygon (fan from first vertex)
      for (let i = 1; i < polyVerts.length - 1; i++) {
        newFaces.push({
          a: polyVerts[0],
          b: polyVerts[i],
          c: polyVerts[i + 1]
        });
      }
    });

    // Create cap faces for each convex (truncated) vertex
    for (let vIdx = 0; vIdx < vertices.length; vIdx++) {
      if (!isConvex.get(vIdx)) continue;

      const neighborMap = cutVertexMap.get(vIdx);
      if (!neighborMap || neighborMap.size < 3) continue;

      const center = vertices[vIdx];

      // Get all cut vertices around this original vertex
      const cutIndices = Array.from(neighborMap.values());
      const cutPositions = cutIndices.map(i => newVertices[i]);

      // Sort cut vertices by angle around the original vertex
      const sorted = this.sortVerticesByAngle(cutPositions, cutIndices, center);

      // Check winding order - cap normal should point outward (toward the original vertex)
      const p0 = newVertices[sorted[0]];
      const p1 = newVertices[sorted[1]];
      const p2 = newVertices[sorted[2]];

      const edge1 = { x: p1.x - p0.x, y: p1.y - p0.y, z: p1.z - p0.z };
      const edge2 = { x: p2.x - p0.x, y: p2.y - p0.y, z: p2.z - p0.z };
      const capNormal = this.cross(edge1, edge2);

      // Direction from cap center toward original vertex
      const capCenter = {
        x: cutPositions.reduce((s, p) => s + p.x, 0) / cutPositions.length,
        y: cutPositions.reduce((s, p) => s + p.y, 0) / cutPositions.length,
        z: cutPositions.reduce((s, p) => s + p.z, 0) / cutPositions.length
      };
      const capToVertex = {
        x: center.x - capCenter.x,
        y: center.y - capCenter.y,
        z: center.z - capCenter.z
      };

      const needsReverse = this.dot(capNormal, capToVertex) <= 0;

      // Triangulate the cap (fan from first vertex)
      for (let i = 1; i < sorted.length - 1; i++) {
        if (needsReverse) {
          newFaces.push({
            a: sorted[0],
            b: sorted[i + 1],
            c: sorted[i]
          });
        } else {
          newFaces.push({
            a: sorted[0],
            b: sorted[i],
            c: sorted[i + 1]
          });
        }
      }
    }

    return { newVertices, newFaces };
  }

  private sortVerticesByAngle(
    positions: Vertex[],
    indices: number[],
    center: Vertex
  ): number[] {
    if (positions.length < 3) return indices;

    // Calculate centroid of cut vertices
    const centroid = {
      x: positions.reduce((s, p) => s + p.x, 0) / positions.length,
      y: positions.reduce((s, p) => s + p.y, 0) / positions.length,
      z: positions.reduce((s, p) => s + p.z, 0) / positions.length
    };

    // Normal from center to centroid (approximate face normal)
    const normal = this.normalize({
      x: centroid.x - center.x,
      y: centroid.y - center.y,
      z: centroid.z - center.z
    });

    // Create orthonormal basis on the cap plane
    const ref = positions[0];
    const toRef = {
      x: ref.x - centroid.x,
      y: ref.y - centroid.y,
      z: ref.z - centroid.z
    };
    const basisX = this.normalize(toRef);
    const basisY = this.cross(normal, basisX);

    // Calculate angle for each vertex
    const withAngles = indices.map((idx, i) => {
      const p = positions[i];
      const toP = {
        x: p.x - centroid.x,
        y: p.y - centroid.y,
        z: p.z - centroid.z
      };
      const x = this.dot(toP, basisX);
      const y = this.dot(toP, basisY);
      return { idx, angle: Math.atan2(y, x) };
    });

    // Sort by angle
    withAngles.sort((a, b) => a.angle - b.angle);

    return withAngles.map(v => v.idx);
  }

  private normalize(v: Vertex): Vertex {
    const len = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
    if (len === 0) return { x: 0, y: 0, z: 1 };
    return { x: v.x / len, y: v.y / len, z: v.z / len };
  }

  private cross(a: Vertex, b: Vertex): Vertex {
    return {
      x: a.y * b.z - a.z * b.y,
      y: a.z * b.x - a.x * b.z,
      z: a.x * b.y - a.y * b.x
    };
  }

  private dot(a: Vertex, b: Vertex): number {
    return a.x * b.x + a.y * b.y + a.z * b.z;
  }

  private distance(a: Vertex, b: Vertex): number {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    const dz = a.z - b.z;
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  private computeBounds(vertices: Vertex[]): { min: Vertex; max: Vertex } {
    const min = { x: Infinity, y: Infinity, z: Infinity };
    const max = { x: -Infinity, y: -Infinity, z: -Infinity };

    for (const v of vertices) {
      min.x = Math.min(min.x, v.x);
      min.y = Math.min(min.y, v.y);
      min.z = Math.min(min.z, v.z);
      max.x = Math.max(max.x, v.x);
      max.y = Math.max(max.y, v.y);
      max.z = Math.max(max.z, v.z);
    }

    return { min, max };
  }

  /**
   * Merge vertices that are within tolerance distance of each other.
   * Returns new vertex array and remapping for face indices.
   */
  private weldVertices(
    vertices: Vertex[],
    faces: Face[],
    tolerance: number
  ): { vertices: Vertex[]; faces: Face[] } {
    const merged: Vertex[] = [];
    const indexMap: number[] = new Array(vertices.length);

    for (let i = 0; i < vertices.length; i++) {
      const v = vertices[i];
      let foundIdx = -1;

      // Check if this vertex is close to any already-merged vertex
      for (let j = 0; j < merged.length; j++) {
        if (this.distance(v, merged[j]) < tolerance) {
          foundIdx = j;
          break;
        }
      }

      if (foundIdx >= 0) {
        indexMap[i] = foundIdx;
      } else {
        indexMap[i] = merged.length;
        merged.push(v);
      }
    }

    // Remap face indices
    const newFaces = faces.map(f => ({
      a: indexMap[f.a],
      b: indexMap[f.b],
      c: indexMap[f.c]
    })).filter(f => f.a !== f.b && f.b !== f.c && f.c !== f.a); // Remove degenerate triangles

    return { vertices: merged, faces: newFaces };
  }

  private buildBufferGeometry(vertices: Vertex[], faces: Face[]): BufferGeometry {
    const geometry = new this.three.BufferGeometry();

    // Build position array (non-indexed for simplicity with flat shading)
    const positions: number[] = [];

    for (const face of faces) {
      const a = vertices[face.a];
      const b = vertices[face.b];
      const c = vertices[face.c];

      positions.push(a.x, a.y, a.z);
      positions.push(b.x, b.y, b.z);
      positions.push(c.x, c.y, c.z);
    }

    geometry.setAttribute(
      'position',
      new this.three.Float32BufferAttribute(positions, 3)
    );

    geometry.computeVertexNormals();

    return geometry;
  }

}

export const truncateNodeDef: NodeDef<TruncateNode> = {
  type: 'Truncate',
  shortName: 'truncate',
  info: 'Cuts off vertices to create new facets (e.g. truncated icosahedron, soccer ball).',
  category: 'modifier',
  endpoints: truncateEndpoints,
  ctor: TruncateNode
};
